/**
 * Decides whether to show the "you left something in the basket" prompt.
 *
 * The rule that keeps this from being annoying: it only appears on a **return
 * visit**. Showing it whenever the cart is non-empty would nag someone who just
 * added an item thirty seconds ago, which is the opposite of the brief.
 *
 * "Return visit" = the cart survived from an earlier session. Shopify persists
 * the cart server-side, so a fresh `sessionStorage` with a non-empty cart means
 * exactly that. It cannot be determined in Liquid, which is why visibility is
 * decided here rather than server-side.
 *
 * Dismissal is remembered for 24 hours. A prompt that returns on the next page
 * load is worse than no prompt at all.
 */
const SESSION_KEY = 'fine:session-seen';
const DISMISS_KEY = 'fine:cart-nudge-dismissed';
const DISMISS_FOR_MS = 24 * 60 * 60 * 1000;

/** Storage can throw in private modes; never let that break the page. */
function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch {
    /* no-op */
  }
}

class CartReturnNudge extends HTMLElement {
  connectedCallback() {
    this.dismissButton = this.querySelector('[ref="dismiss"]');
    this.viewButton = this.querySelector('[ref="view"]');

    // Mark the session before any early return. This must happen on every page
    // load — including empty carts and the cart page — or adding a first item
    // looks like a brand new session and the nudge fires immediately.
    const isReturnVisit = !safeGet(sessionStorage, SESSION_KEY);
    safeSet(sessionStorage, SESSION_KEY, '1');

    const itemCount = Number(this.dataset.itemCount) || 0;

    if (this.dataset.suppress === 'true') return; // cart page: already there
    if (!isReturnVisit || itemCount === 0 || this.#recentlyDismissed()) return;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.dismiss = this.dismiss.bind(this);

    this.dismissButton?.addEventListener('click', this.dismiss);
    // Opening the basket counts as acting on it — don't nag again after.
    this.viewButton?.addEventListener('click', this.dismiss);
    document.addEventListener('keydown', this.handleKeydown);

    // A brief delay keeps it from competing with first paint.
    this.timer = window.setTimeout(() => {
      this.hidden = false;
    }, 1200);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  #recentlyDismissed() {
    const stamp = Number(safeGet(localStorage, DISMISS_KEY));
    if (!stamp) return false;
    return Date.now() - stamp < DISMISS_FOR_MS;
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.key === 'Escape' && !this.hidden) this.dismiss();
  }

  dismiss() {
    safeSet(localStorage, DISMISS_KEY, String(Date.now()));
    this.hidden = true;
    clearTimeout(this.timer);
    document.removeEventListener('keydown', this.handleKeydown);
  }
}

if (!customElements.get('cart-return-nudge')) {
  customElements.define('cart-return-nudge', CartReturnNudge);
}
